import React, { useEffect, useState, useMemo } from 'react';
import { productService, clientService, categoryService } from '../services/api';
import CategoryManager from '../components/CategoryManager';
import ImageUpload from '../components/ImageUpload';

const EMPTY = { name: '', price: '', cost: '', stock: 0, min_stock: 0, category_id: '', image_url: '' };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null);   // {type:'form'|'sell', product?}
  const [catModal, setCatModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sell, setSell] = useState({ quantity: 1, payment_method: 'cash', client_id: '' });
  const [loading, setLoading] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);

  const load = async () => {
    const [p, c, cat] = await Promise.all([productService.getAll(), clientService.getAll(), categoryService.getAll('product')]);
    setProducts(p.data);
    setClients(c.data);
    setCategories(cat.data);
  };
  useEffect(() => { load(); }, []);

  const filteredProducts = useMemo(() => {
    const s = search.trim().toLowerCase();
    return products.filter(p => {
      const matchSearch = !s ||
        (p.name && p.name.toLowerCase().includes(s)) ||
        (p.category && p.category.toLowerCase().includes(s));
      const matchCat = !selectedCategory || String(p.category_id) === String(selectedCategory);
      return matchSearch && matchCat;
    });
  }, [products, search, selectedCategory]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 10);
    const term = clientSearch.trim().toLowerCase();
    return clients.filter(c =>
      (c.name && c.name.toLowerCase().includes(term)) ||
      (c.nickname && c.nickname.toLowerCase().includes(term)) ||
      (c.phone && c.phone.includes(term))
    ).slice(0, 20);
  }, [clients, clientSearch]);

  const openNew = () => { setForm(EMPTY); setModal({ type: 'form' }); };
  const openEdit = (p) => {
    setForm({ name: p.name, price: p.price, cost: p.cost, stock: p.stock, min_stock: p.min_stock, category_id: p.category_id || '', image_url: p.image_url || '' });
    setModal({ type: 'form', product: p });
  };
  const openSell = (p) => {
    setSell({ quantity: 1, payment_method: 'cash', client_id: '' });
    setClientSearch('');
    setClientDropdownOpen(false);
    setModal({ type: 'sell', product: p });
  };

  const handleSave = async () => {
    if (!form.name || form.price === '') { alert('Nome e preço são obrigatórios'); return; }
    setLoading(true);
    try {
      if (modal.product) await productService.update(modal.product.id, form);
      else await productService.create(form);
      setModal(null); load();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  const handleImageChange = async (url) => {
    const next = { ...form, image_url: url };
    setForm(next);
    // Em produto existente, o upload já deve persistir a foto. O usuário não
    // precisa descobrir que ainda teria de clicar em Salvar depois de enviá-la.
    if (!modal?.product) return;
    setImageSaving(true);
    try {
      await productService.update(modal.product.id, next);
      await load();
    } catch (err) {
      alert('Erro ao salvar imagem: ' + (err.response?.data?.error || err.message));
    } finally {
      setImageSaving(false);
    }
  };

  const handleSell = async () => {
    setLoading(true);
    try {
      const res = await productService.sell(modal.product.id, {
        quantity: parseInt(sell.quantity),
        payment_method: sell.payment_method,
        client_id: sell.client_id || null,
      });
      alert(`Venda registrada! Total R$ ${res.data.total} · estoque restante: ${res.data.remaining_stock}`);
      setModal(null); load();
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Remover "${p.name}"?`)) return;
    await productService.delete(p.id); load();
  };

  const money = (v) => `R$ ${Number(v).toFixed(2)}`;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Lanchonete</h1>
          <p style={styles.sub}>{products.length} produtos • {products.filter(p => p.low_stock).length} com estoque baixo</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={styles.btnSecondary} onClick={() => setCatModal(true)}>Categorias</button>
          <button style={styles.btnPrimary} onClick={openNew}>+ Produto</button>
        </div>
      </div>

      <div style={styles.searchBar}>
        <div style={styles.searchContainer}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Pesquisar por nome do produto ou categoria..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button style={styles.clearBtn} onClick={() => setSearch('')} title="Limpar pesquisa">✕</button>
          )}
        </div>
        <select
          style={styles.categorySelect}
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {(search || selectedCategory) && (
        <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
          Exibindo {filteredProducts.length} de {products.length} produtos
        </div>
      )}

      <div style={styles.grid}>
        {filteredProducts.map(p => (
          <div key={p.id} style={styles.card}>
            <div style={styles.cardTop}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {p.image_url
                  ? <img src={p.image_url} alt={p.name} style={styles.thumb} />
                  : <div style={{ ...styles.thumb, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4b5563', fontSize: 20 }}>🍔</div>}
                <div>
                  <div style={styles.name}>{p.name}</div>
                  <div style={styles.cat}>{p.category || 'Sem categoria'}</div>
                </div>
              </div>
              {Number(p.low_stock) === 1 && <span style={styles.lowBadge}>estoque baixo</span>}
            </div>
            <div style={styles.priceRow}>
              <span style={styles.price}>{money(p.price)}</span>
              <span style={styles.margin}>margem {money(p.margin)}</span>
            </div>
            <div style={styles.stockRow}>
              <span style={{ color: Number(p.low_stock) === 1 ? '#f87171' : '#9ca3af' }}>
                Estoque: {p.stock}
              </span>
              <span style={styles.cost}>custo {money(p.cost)}</span>
            </div>
            <div style={styles.actions}>
              <button style={styles.btnSell} onClick={() => openSell(p)}>💵 Vender</button>
              <button style={styles.btnGhost} onClick={() => openEdit(p)}>Editar</button>
              <button style={styles.btnGhostDanger} onClick={() => handleDelete(p)}>✕</button>
            </div>
          </div>
        ))}
        {!filteredProducts.length && (
          <div style={styles.empty}>
            {products.length === 0
              ? 'Nenhum produto. Clique em “+ Produto”.'
              : `Nenhum produto encontrado${search ? ` para "${search}"` : ''}.`}
            {(search || selectedCategory) && (
              <button
                style={{ ...styles.btnSecondary, display: 'block', margin: '12px auto 0' }}
                onClick={() => { setSearch(''); setSelectedCategory(''); }}
              >
                Limpar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* MODAL FORM */}
      {modal?.type === 'form' && (
        <Overlay onClose={() => setModal(null)} title={modal.product ? 'Editar Produto' : 'Novo Produto'}>
          <div style={styles.modalGrid}>
            <Field label="Nome *" full><input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Preço de venda *"><input type="number" step="0.01" style={styles.input} value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Custo"><input type="number" step="0.01" style={styles.input} value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></Field>
            <Field label="Estoque"><input type="number" style={styles.input} value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} /></Field>
            <Field label="Estoque mínimo"><input type="number" style={styles.input} value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} /></Field>
            <Field label="Categoria" full>
              <select style={styles.input} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                <option value="">Sem categoria</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ marginBottom: 14 }}>
            <ImageUpload label="Foto do produto (ilustração vende mais)"
              value={form.image_url} onChange={handleImageChange} />
            {modal.product && <div style={{ color: imageSaving ? '#f59e0b' : '#6b7280', fontSize: 12, marginTop: 6 }}>
              {imageSaving ? 'Salvando imagem no produto…' : 'A imagem enviada é salva automaticamente.'}
            </div>}
          </div>
          <Actions onCancel={() => setModal(null)} onSave={handleSave} loading={loading || imageSaving} />
        </Overlay>
      )}

      {/* MODAL SELL */}
      {modal?.type === 'sell' && (
        <Overlay onClose={() => setModal(null)} title={`Vender — ${modal.product.name}`}>
          <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 14 }}>
            {money(modal.product.price)} un. · estoque {modal.product.stock}
          </div>
          <Field label="Quantidade">
            <input type="number" min="1" max={modal.product.stock} style={styles.input} value={sell.quantity}
              onChange={e => setSell({ ...sell, quantity: e.target.value })} />
            <div style={{ color: 'var(--accent)', fontSize: 14, marginTop: 6, fontWeight: 600 }}>
              Total: {money((Number(sell.quantity) || 0) * Number(modal.product.price))}
            </div>
          </Field>
          <Field label="Pagamento">
            <select style={styles.input} value={sell.payment_method} onChange={e => setSell({ ...sell, payment_method: e.target.value })}>
              <option value="cash">Dinheiro</option>
              <option value="pix">PIX</option>
              <option value="card_debit">Cartão débito</option>
              <option value="card_credit">Cartão crédito</option>
            </select>
          </Field>
          <Field label="Cliente (opcional — se ficar vazio entra como Sem Cadastro)">
            {sell.client_id ? (
              <div style={styles.selectedClientBadge}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ClientAvatar client={clients.find(c => String(c.id) === String(sell.client_id))} size={32} />
                  <div>
                    <strong style={{ color: '#fff', fontSize: 14 }}>{clients.find(c => String(c.id) === String(sell.client_id))?.name || 'Cliente selecionado'}</strong>
                    {clients.find(c => String(c.id) === String(sell.client_id))?.nickname && (
                      <span style={{ fontSize: 12, color: 'var(--accent)', marginLeft: 6 }}>
                        (@{clients.find(c => String(c.id) === String(sell.client_id))?.nickname})
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  style={styles.clearClientBtn}
                  onClick={() => { setSell({ ...sell, client_id: '' }); setClientSearch(''); }}
                  title="Remover cliente"
                >
                  ✕ Remover
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="🔍 Digite para pesquisar cliente (ou deixe vazio)..."
                  value={clientSearch}
                  onFocus={() => setClientDropdownOpen(true)}
                  onChange={e => {
                    setClientSearch(e.target.value);
                    setClientDropdownOpen(true);
                  }}
                />
                {clientDropdownOpen && (
                  <>
                    <div style={styles.dropdownBackdrop} onClick={() => setClientDropdownOpen(false)} />
                    <div style={styles.clientDropdown}>
                      <div
                        style={styles.clientOptionDefault}
                        onClick={() => {
                          setSell({ ...sell, client_id: '' });
                          setClientSearch('');
                          setClientDropdownOpen(false);
                        }}
                      >
                        🚫 Sem cadastro (venda sem nome)
                      </div>
                      {filteredClients.map(c => (
                        <div
                          key={c.id}
                          style={styles.clientOption}
                          onClick={() => {
                            setSell({ ...sell, client_id: c.id });
                            setClientSearch(c.name);
                            setClientDropdownOpen(false);
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <ClientAvatar client={c} size={28} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: '#fff' }}>
                                {c.name} {c.nickname ? <span style={{ color: 'var(--accent)', fontWeight: 400, fontSize: 12 }}>(@{c.nickname})</span> : ''}
                              </div>
                              {c.phone && <div style={{ fontSize: 11, color: '#6b7280' }}>📱 {c.phone}</div>}
                            </div>
                          </div>
                        </div>
                      ))}
                      {filteredClients.length === 0 && clientSearch && (
                        <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: 13 }}>
                          Nenhum cliente encontrado com "{clientSearch}". A venda será sem cadastro.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </Field>
          <div style={styles.modalActions}>
            <button style={styles.btnGhost} onClick={() => setModal(null)}>Cancelar</button>
            <button style={styles.btnSell} onClick={handleSell} disabled={loading}>{loading ? '...' : 'Confirmar venda'}</button>
          </div>
        </Overlay>
      )}

      {catModal && (
        <CategoryManager kind="product" title="Categorias da Lanchonete"
          onClose={() => setCatModal(false)} onChange={load} />
      )}
    </div>
  );
}

function ClientAvatar({ client, size = 30 }) {
  const [imgError, setImgError] = useState(false);
  const name = client?.name || '';
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const url = client?.avatar_url;

  const getColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#10b981', '#06b6d4', '#3b82f6', '#f59e0b'];
    return colors[Math.abs(hash) % colors.length];
  };

  if (url && !imgError) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #3b3b4c',
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: getColor(name),
        color: '#fff',
        fontWeight: 700,
        fontSize: Math.max(11, Math.round(size * 0.45)),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.15)',
        textTransform: 'uppercase',
      }}
    >
      {initial}
    </div>
  );
}

function Overlay({ title, children, onClose }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children, flex, full }) {
  return <div style={{ marginBottom: 14, ...(flex ? { flex: 1 } : {}), ...(full ? styles.fullSpan : {}) }}><label style={styles.label}>{label}</label>{children}</div>;
}
function Actions({ onCancel, onSave, loading }) {
  return (
    <div style={styles.modalActions}>
      <button style={styles.btnGhost} onClick={onCancel}>Cancelar</button>
      <button style={styles.btnPrimary} onClick={onSave} disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</button>
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#6b7280', fontSize: 14, margin: '4px 0 0' },
  btnPrimary: { padding: '10px 18px', background: 'var(--accent)', color: '#0a0a0f', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnSecondary: { padding: '10px 18px', background: 'transparent', border: '1px solid #374151', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 14 },
  searchBar: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchContainer: { position: 'relative', flex: 1, minWidth: 240, display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: 12, color: '#6b7280', fontSize: 14, pointerEvents: 'none' },
  searchInput: { width: '100%', padding: '10px 36px 10px 36px', background: '#14141f', border: '1px solid #262636', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  clearBtn: { position: 'absolute', right: 10, background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, padding: 4 },
  categorySelect: { padding: '10px 14px', background: '#14141f', border: '1px solid #262636', borderRadius: 8, color: '#fff', fontSize: 14, minWidth: 180, outline: 'none', cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  card: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  thumb: { width: 46, height: 46, borderRadius: 10, objectFit: 'cover', background: '#0e0e16', border: '1px solid #262636', flexShrink: 0 },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  name: { color: '#fff', fontWeight: 700, fontSize: 16 },
  cat: { color: '#6b7280', fontSize: 12, marginTop: 2 },
  lowBadge: { background: '#450a0a', color: '#f87171', fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 20 },
  priceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  price: { color: '#34d399', fontSize: 20, fontWeight: 700 },
  margin: { color: '#6b7280', fontSize: 12 },
  stockRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13 },
  cost: { color: '#6b7280', fontSize: 12 },
  actions: { display: 'flex', gap: 8, marginTop: 4 },
  btnSell: { flex: 1, padding: '9px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnGhost: { padding: '9px 12px', background: 'transparent', border: '1px solid #374151', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 13 },
  btnGhostDanger: { padding: '9px 11px', background: 'transparent', border: '1px solid #7f1d1d', borderRadius: 8, color: '#f87171', cursor: 'pointer', fontSize: 13 },
  empty: { color: '#6b7280', textAlign: 'center', padding: 40, gridColumn: '1 / -1' },
  overlay: { position: 'fixed', inset: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 50, padding: '24px 12px' },
  modal: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: 28, width: 700, maxWidth: 'calc(100vw - 28px)', maxHeight: 'calc(100dvh - 48px)', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 20px' },
  modalGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0 12px' },
  fullSpan: { gridColumn: '1 / -1' },
  label: { display: 'block', color: '#9ca3af', fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 },
  selectedClientBadge: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#38bdf8', fontSize: 14 },
  clearClientBtn: { background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  dropdownBackdrop: { position: 'fixed', inset: 0, zIndex: 80 },
  clientDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 90, background: '#14141f', border: '1px solid #374151', borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' },
  clientOptionDefault: { padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #262636', color: '#9ca3af', fontStyle: 'italic', fontSize: 13 },
  clientOption: { padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #262636', display: 'flex', flexDirection: 'column', gap: 2 },
};
